<template>
  <q-card flat bordered>
    <q-card-section v-if="showAddSubjectButton">
      <q-btn
        unelevated
        dense
        flat
        no-caps
        color="primary"
        icon="add"
        @click="skippedSubject = false"
      >
        Add Subject
      </q-btn>
    </q-card-section>
    <q-card-section v-else-if="report.subject === null">
      <div class="text-h6 q-mb-md">Enter the Subject Property</div>
      <address-autocomplete
        id="intercom-enter-subject-address"
        placeholder="Enter an address or building name"
        surface-reports
        autofocus
        show-icon
        @new-address="newSubject"
      />
      <q-card-section class="q-px-none row items-center justify-between">
        <q-btn
          v-if="canCreateNewDevelopments"
          active-class="text-primary indented-primary"
          no-caps
          unelevated
          @click="newCustomProperty"
        >
          <div class="flex items-center">
            <q-icon name="add_circle" class="q-mr-sm" />
            New Development
          </div>
        </q-btn>
        <q-btn flat no-caps unelevated color="red" @click="skipSubjectEntry">
          Skip Subject Property
        </q-btn>
      </q-card-section>
    </q-card-section>
    <q-card-section v-if="report.subject || report.comparable_addresses.length">
      <property-compare-google-map
        :subject-detail="subjectDetail"
        :filtered-properties="filteredProperties"
        :next-candidates="nextCandidates"
        :loading="loading"
        :similarities="similarities"
        delete-enabled
        @add-candidate="addCandidateFromMap"
        @delete-address="deleteAddress"
      />
      <property-compare-building-info
        :subject-detail="subjectDetail"
        :filtered-properties="filteredProperties"
        :loading="loading"
        :similarities="similarities"
        :date-range="dateRange"
        :skeleton-rows="report.comparable_addresses.length + (report.subject !== null ? 1 : 0)"
        :report-id="report.id"
        :visible-columns="getVisibleColumnsForSection(visibleColumns, Section.BUILDING_INFORMATION)"
        :toggleable-columns="getToggleableColumnsForSection(Section.BUILDING_INFORMATION)"
        delete-enabled
        class="q-mt-md"
        @delete-address="deleteAddress"
        @move-comp="moveComp"
        @update-property-details="updatePropertyDetails"
      />
    </q-card-section>
    <q-card-section v-if="preventAddingSingleComp">
      <warning-banner class="q-mb-md">
        For faster loading speeds, we do not allow reports with more than
        {{ maxCompLimit }} comps.
      </warning-banner>
    </q-card-section>
    <q-card-section v-else-if="report.subject || skippedSubject" id="add-rent-comps">
      <div class="flex justify-between q-mb-md">
        <div class="text-h6">Rent Comps</div>
        <div class="q-gutter-x-sm">
          <q-btn
            no-caps
            unelevated
            :disable="
              report.excluded_property_ids.length + report.comparable_addresses.length === 0
            "
            @click="resetSelections()"
          >
            Reset Selections
            <q-tooltip
              v-if="report.excluded_property_ids.length + report.comparable_addresses.length === 0"
              >No properties to reset.</q-tooltip
            >
            <q-tooltip v-else>
              Reset the selections for the subject and comps. This will remove all properties from
              the report.
            </q-tooltip>
          </q-btn>
          <q-btn no-caps outline @click="advancedMode = !advancedMode">
            Comp Settings
            <q-badge v-if="countFilters" class="q-ml-sm">
              {{ countFilters }}
            </q-badge>
            <slot name="after">
              <q-icon v-if="!advancedMode" name="arrow_drop_down" />
              <q-icon v-else name="arrow_drop_up" />
            </slot>
          </q-btn>
          <q-btn
            id="intercom-suggest-comps"
            :disable="!report.subject"
            :loading="compsLoading"
            :percentage="percentageLoaded"
            :label="`Suggest${report.comparable_addresses.length > 0 && report.subject ? ' More' : ''} Comps`"
            no-caps
            unelevated
            split
            color="primary"
            class="relative-position"
            @click="suggestComps()"
          >
            <ping-dot v-if="$route.hash === '#add-rent-comps' && showButtonPingDot" />
          </q-btn>
          <q-tooltip v-if="!report.subject">Enter the subject to enable this feature.</q-tooltip>
        </div>
      </div>

      <advanced-settings
        v-if="advancedMode"
        v-model:settings="customSettings"
        @reset-settings="resetSettings"
      />

      <address-autocomplete
        id="intercom-enter-comp-address"
        :placeholder="`Enter an${
          report.comparable_addresses.length > 0 ? 'other' : ''
        } address or building name`"
        :lat="subjectDetail?.lat"
        :lon="subjectDetail?.lon"
        show-icon
        @new-address="newAddress"
      />
    </q-card-section>
  </q-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { addDays } from 'date-fns'

import {
  type PropertyDetailsResponse,
  type PropertySearchResponse,
  type PropertyReportSubscriptionAttributes,
  Section,
  SectionColumn,
  getLatestPricesByFilter,
  overwritePropertyData,
  getUnitsByFilter,
  type Similarities,
  maxCompLimit,
  defaultColumns,
  type PropertyComparableResponse
} from 'common'

import { scroll } from 'quasar'

import { useUserStore } from '@/stores/user'
import { usePropertyStore } from '@/stores/property'

import AddressAutocomplete from '@/components/PropertyReports/AddressAutocomplete.vue'
import PropertyCompareGoogleMap from '@/components/PropertyReports/PropertyCompareGoogleMap.vue'
import PropertyCompareBuildingInfo from '@/components/PropertyReports/PropertyCompareBuildingInfo.vue'
import AdvancedSettings from '@/components/PropertyReports/AdvancedSettings.vue'
import PingDot from '@/components/PingDot.vue'
import WarningBanner from '@/components/WarningBanner.vue'

import {
  compResponseToSearch,
  formatPercent,
  getToggleableColumnsForSection,
  getVisibleColumnsForSection
} from '@/utils'

import { useStripeStore } from '@/stores/stripe'
const { getScrollTarget, setVerticalScrollPosition } = scroll

const moveArrayElement = <T,>(arr: T[], from: number, to: number): T[] => {
  const element = arr[from]
  const withoutElement = [...arr.slice(0, from), ...arr.slice(from + 1)]
  return [...withoutElement.slice(0, to), element, ...withoutElement.slice(to)]
}

const defaultSettings = {
  maxRadius: null as number | null,
  minUnits: null as number | null,
  maxUnits: null as number | null,
  minYearBuilt: null as number | null,
  maxYearBuilt: null as number | null,
  minNumberStories: null as number | null,
  maxNumberStories: null as number | null,
  valueAdd: null as number | null,
  useEmbedding: null as boolean | null,
  isAffordable: null as boolean | null,
  isSenior: null as boolean | null,
  isStudent: null as boolean | null,
  maxMilesToUniversity: null as number | null,
  maxMilesToHospital: null as number | null
}

export default {
  name: 'EnterAddresses',
  components: {
    WarningBanner,
    PingDot,
    AddressAutocomplete,
    PropertyCompareGoogleMap,
    PropertyCompareBuildingInfo,
    AdvancedSettings
  },
  props: {
    report: {
      type: Object as PropType<PropertyReportSubscriptionAttributes>,
      required: true
    }
  },
  emits: ['update-property-details', 'update-report', 'new-custom-property'],
  watch: {
    customSettings: {
      deep: true,
      handler() {
        // if someone changes the settings, we drop pre-cached comps, since they are not valid anymore
        this.nextCandidates = []
        this.$emit('update-report', this.reportCopy)
      }
    }
  },
  data() {
    return {
      Section,
      formatPercent,
      maxCompLimit,
      compsLoading: false,
      compsLoadingState: null as { current: number; total: number } | null,
      loading: true,
      showButtonPingDot: true,
      subjectDetail: null as PropertyDetailsResponse | null,
      comparableDetails: [] as PropertyDetailsResponse[],
      similarities: {} as { [key: string]: Similarities },
      advancedMode: false,
      customSettings: defaultSettings,
      dateRange: {
        from: addDays(new Date(), -30),
        to: new Date()
      },
      reportCopy: this.report,
      skippedSubject: false,
      nextCandidates: [] as PropertyComparableResponse[]
    }
  },
  computed: {
    percentageLoaded() {
      if (this.compsLoadingState) {
        return (this.compsLoadingState.current / this.compsLoadingState.total) * 100
      }
      return undefined
    },
    allProperties() {
      if (this.subjectDetail) {
        return [this.subjectDetail, ...this.comparableDetails]
      }
      return this.comparableDetails
    },
    filteredProperties() {
      return this.allProperties.flatMap((prop) => {
        return {
          prop,
          units: getUnitsByFilter(
            prop,
            getLatestPricesByFilter(prop, this.dateRange.from, this.dateRange.to),
            this.dateRange.to,
            'all'
          )
        }
      })
    },
    countFilters() {
      return [
        this.customSettings.maxRadius,
        this.customSettings.minUnits,
        this.customSettings.maxUnits,
        this.customSettings.minYearBuilt,
        this.customSettings.maxYearBuilt,
        this.customSettings.minNumberStories,
        this.customSettings.maxNumberStories,
        this.customSettings.valueAdd !== 0 ? this.customSettings.valueAdd : null,
        this.customSettings.isAffordable !== null ? this.customSettings.isAffordable : null,
        this.customSettings.isSenior !== null ? this.customSettings.isSenior : null,
        this.customSettings.isStudent !== null ? this.customSettings.isStudent : null,
        this.customSettings.maxMilesToUniversity !== null
          ? this.customSettings.maxMilesToUniversity
          : null,
        this.customSettings.maxMilesToHospital !== null
          ? this.customSettings.maxMilesToHospital
          : null
      ].filter((_) => _ !== null).length
    },
    user() {
      return this.$auth0.user?.value
    },
    visibleColumns(): SectionColumn[] {
      return this.report?.columns ?? defaultColumns
    },
    showAddSubjectButton() {
      return this.skippedSubject && this.report.subject === null
    },
    preventAddingSingleComp() {
      return this.comparableDetails.length > this.maxCompLimit - 1
    },
    canCreateNewDevelopments() {
      return useUserStore().canCreateNewDevelopments
    },
    billedPerUnit() {
      return useStripeStore().billedPerUnit
    }
  },
  created() {
    if (this.report.subject) {
      this.updateSubject().then(() => {
        if (this.report.id === -1) {
          // auto-load comps if we are creating a new report
          // with a seeded subject
          this.suggestComps()
        }
      })
    }
    // If a report doesn't have a subject but has at least one comp, it means they skipped the subject previously
    if (!this.report.subject && this.report.comparable_addresses.length > 0) {
      this.skippedSubject = true
    }
    if (this.comparableDetails.length !== this.report.comparable_addresses.length) {
      this.updateComparables()
    }
  },
  mounted() {
    if (this.$route.hash) {
      const el = document.querySelector(this.$route.hash) as HTMLElement | null
      if (el) {
        const target = getScrollTarget(el)
        const offset = el.offsetTop
        const duration = 1000
        setVerticalScrollPosition(target, offset, duration)
      }
    }
  },
  methods: {
    getToggleableColumnsForSection,
    getVisibleColumnsForSection,
    moveComp(fromIndex: number, toIndex: number) {
      if (fromIndex === toIndex) return

      if (this.subjectDetail) {
        fromIndex = fromIndex - 1
        toIndex = toIndex - 1
      }

      this.reportCopy.comparable_addresses = moveArrayElement(
        this.reportCopy.comparable_addresses,
        fromIndex,
        toIndex
      )

      this.comparableDetails = moveArrayElement(this.comparableDetails, fromIndex, toIndex)

      this.$emit('update-report', this.reportCopy)
      const direction = fromIndex < toIndex ? 'up' : 'down'
      this.$tracker.movedComp(fromIndex, direction)
    },
    resetSettings() {
      this.customSettings = defaultSettings
    },
    async refreshSimilarities() {
      if (this.subjectDetail && this.comparableDetails.length) {
        this.similarities = {}
        await usePropertyStore()
          .comparables(this.subjectDetail, {
            excludedIds: this.report.excluded_property_ids,
            selectedIds: this.report.comparable_addresses.map((_) => _.id),
            topN: this.maxCompLimit,
            renovations: this.customSettings.valueAdd ?? 0
          })
          .then((comps) => {
            comps.comparables.forEach((comp) => {
              this.similarities[comp.id] = comp.similarity_score
            })
            // Update nextCandidates with comps not already selected in the report
            const nextCandidates = comps.comparables.filter(
              (comp) => !this.report.comparable_addresses.find((_) => _.id === comp.id)
            )
            this.nextCandidates = nextCandidates
          })
      }
    },
    resetSelections() {
      this.reportCopy.excluded_property_ids = []
      this.reportCopy.comparable_addresses = []
      this.comparableDetails = []
      this.nextCandidates = []
      this.$emit('update-report', this.reportCopy)
    },
    updatePropertyDetails(propertyId: string, key: string, value: string | number) {
      // change subjectDetail or comparableDetails property's key to value
      if (this.subjectDetail && this.subjectDetail.id === propertyId) {
        this.subjectDetail[key] = value
      } else {
        const detail = this.comparableDetails.find((_) => _.id === propertyId)
        if (detail) {
          detail[key] = value
        }
      }
      this.$emit('update-property-details', propertyId, key, value)
    },
    async updateComparables() {
      const existingDetailIds = new Set(this.comparableDetails.map((_) => _.id))
      const todo = this.report.comparable_addresses.filter(
        (address) => !existingDetailIds.has(address.id)
      )

      this.compsLoadingState = {
        current: 0,
        total: todo.length
      }

      if (todo.length) {
        await Promise.all(
          todo.map((_) =>
            usePropertyStore()
              .details(_.id)
              .finally(() => {
                if (this.compsLoadingState) {
                  this.compsLoadingState.current += 1
                }
              })
          )
        )
          .then(async (details) => {
            const combined = this.comparableDetails.concat(
              details.filter((_) => _) as PropertyDetailsResponse[]
            )
            const compsDetails = this.report.comparable_addresses
              .map((address) => combined.find((_) => _.id === address.id))
              .filter((_) => _) as PropertyDetailsResponse[]

            compsDetails.forEach((comp) => {
              overwritePropertyData(comp, this.report?.property_edits ?? [])
            })
            this.comparableDetails = compsDetails
            this.checkLoading()
            await this.refreshSimilarities()
          })
          .then(() => {
            this.compsLoading = false
            this.compsLoadingState = null
          })
      } else {
        this.comparableDetails = this.report.comparable_addresses
          .map((address) => this.comparableDetails.find((_) => _.id === address.id))
          .filter((_) => _) as PropertyDetailsResponse[]
        await this.refreshSimilarities()
        this.compsLoading = false
        this.compsLoadingState = null
      }
    },
    async updateSubject() {
      if (this.report.subject && this.subjectDetail?.id !== this.report.subject.id) {
        return usePropertyStore()
          .details(this.report.subject.id)
          .then(async (prop) => {
            prop = overwritePropertyData(prop, this.report?.property_edits ?? [])

            this.subjectDetail = prop
            this.checkLoading()
            await this.refreshSimilarities()
          })
          .catch((error) => {
            console.error(error)
          })
      }
    },
    newCustomProperty() {
      this.$emit('new-custom-property', {
        name: 'New Development',
        query: { gotoEditNext: true }
      })
    },
    async suggestComps() {
      if (!this.subjectDetail) {
        return
      }
      this.showButtonPingDot = false
      this.compsLoading = true

      this.$tracker.requestedComps(this.customSettings)

      if (this.nextCandidates.length > 0) {
        // add the next 10 candidates
        const missingTenCandidates = this.nextCandidates.slice(0, 10)
        await this.newAddresses(missingTenCandidates.map(compResponseToSearch))
      } else {
        // we need to list all next comps that are not in the report
        await usePropertyStore()
          .comparables(this.subjectDetail, {
            excludedIds: this.report.excluded_property_ids,
            selectedIds: this.report.comparable_addresses.map((_) => _.id),
            topN: this.maxCompLimit,
            renovations: this.customSettings.valueAdd ?? 0,
            maxDistance: this.customSettings.maxRadius || undefined,
            minNumberUnits: this.customSettings.minUnits || undefined,
            maxNumberUnits: this.customSettings.maxUnits || undefined,
            minYearBuilt: this.customSettings.minYearBuilt || undefined,
            maxYearBuilt: this.customSettings.maxYearBuilt || undefined,
            minNumberStories: this.customSettings.minNumberStories || undefined,
            maxNumberStories: this.customSettings.maxNumberStories || undefined,
            useEmbedding: this.customSettings.useEmbedding ?? false,
            isAffordable: this.customSettings.isAffordable ?? undefined,
            isSenior: this.customSettings.isSenior ?? undefined,
            isStudent: this.customSettings.isStudent ?? undefined,
            maxMilesToUniversity: this.customSettings.maxMilesToUniversity ?? undefined,
            maxMilesToHospital: this.customSettings.maxMilesToHospital ?? undefined
          })
          .then(async (resp) => {
            if (resp.comparables.length === 0) {
              this.$q.notify({
                message: 'No comps found. Try adjusting your settings.',
                type: 'positive',
                position: 'top'
              })
            }
            resp.comparables.forEach((comp) => {
              this.similarities[comp.id] = comp.similarity_score
            })
            const nextCandidates = resp.comparables.filter(
              (comp) => !this.report.comparable_addresses.find((_) => _.id === comp.id)
            )
            // add the next 10 candidates
            const missingTenCandidates = nextCandidates.slice(0, 10).map(compResponseToSearch)
            await this.newAddresses(missingTenCandidates)
            this.nextCandidates = nextCandidates.slice(10)
          })
          .catch(() => {
            this.compsLoading = false
          })
      }
    },
    async newAddresses(payload: PropertySearchResponse[]) {
      const filteredNewAddresses = payload.filter(
        (addr) => !this.report.comparable_addresses.some((_) => _.id === addr.id)
      )

      this.reportCopy.comparable_addresses =
        this.reportCopy.comparable_addresses.concat(filteredNewAddresses)

      this.reportCopy.excluded_property_ids = this.reportCopy.excluded_property_ids.filter(
        (_) => !filteredNewAddresses.find((addr) => addr.id === _)
      )
      await this.updateComparables()
      this.$emit('update-report', this.reportCopy)
    },
    async addCandidateFromMap(candidate: PropertyComparableResponse) {
      // remove it from nextCandidates if present
      this.nextCandidates = this.nextCandidates.filter((c) => c.id !== candidate.id)

      // reuse newAddresses flow; candidate shape matches PropertySearchResponse for required fields
      await this.newAddresses([compResponseToSearch(candidate)])
    },
    async newAddress(payload: PropertySearchResponse) {
      if (!this.preventAddingSingleComp) {
        if (this.subjectDetail?.id === payload.id) {
          this.$q.notify({
            message: 'Cannot add comp that is the same property as the subject.',
            color: 'negative',
            position: 'bottom'
          })
        } else {
          await this.newAddresses([payload])
        }
      }
    },
    async newSubject(payload: PropertySearchResponse) {
      const comp = this.report.comparable_addresses.find((addr) => addr.id === payload.id)
      if (comp) {
        this.deleteAddress(comp.id)
      }
      this.reportCopy.subject = payload
      await this.updateSubject()
      this.$emit('update-report', this.reportCopy)
    },
    deleteAddress(rowId: string) {
      if (this.subjectDetail && rowId === this.subjectDetail.id) {
        this.reportCopy.is_in_portfolio = null
        this.reportCopy.subject = null
        this.subjectDetail = null
        this.similarities = {}
        if (this.report.comparable_addresses.length > 0) {
          this.skippedSubject = true
        }
      } else {
        this.reportCopy.comparable_addresses = this.reportCopy.comparable_addresses.filter(
          (_) => _.id !== rowId
        )
        this.reportCopy.excluded_property_ids = this.reportCopy.excluded_property_ids.concat([
          rowId
        ])
        this.comparableDetails = this.comparableDetails.filter((_) => _.id !== rowId)
        this.$tracker.excludedComp(this.similarities[rowId])
      }
      this.$emit('update-report', this.reportCopy)
    },
    checkLoading() {
      if (this.comparableDetails.length === this.report.comparable_addresses.length) {
        if (!this.report.subject || this.subjectDetail) {
          this.loading = false
        }
      }
    },
    skipSubjectEntry() {
      this.$tracker.skippedSubjectEntry()
      this.skippedSubject = true
    },
    renovationLabelValue(value: number) {
      if (value === 0) {
        return 'No Quality Change'
      } else if (value > 0) {
        return `Quality Change: +${(value * 50).toFixed(0)}%`
      } else {
        return `Quality Change: ${(value * 50).toFixed(0)}%`
      }
    }
  }
}
</script>
<style scoped>
.property-compare {
  max-width: 1600px;
}
</style>
